import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Legal.css';

const HowItWorks: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <div className="legal-container">
        <button className="back-btn" onClick={() => navigate('/')}>
          ← Back to Home
        </button>

        <h1>How It Works</h1>
        <p className="last-updated">Create your professional storybook in four simple steps</p>

        <section className="legal-section">
          <h2>Step 1: Plan Your Story</h2>
          <p>
            Start by creating a new storybook project. Choose your book format (portrait, landscape, or square) 
            and specify the number of pages you want. Then use our AI-powered plot planner to outline your story.
          </p>
          <ul>
            <li><strong>Story Wizard:</strong> Define your story type (fairy tale, adventure, educational, etc.), tone (heartwarming, exciting, funny), and art style</li>
            <li><strong>AI Plot Generation:</strong> Let our AI suggest page-by-page storylines based on your description</li>
            <li><strong>Plot Customization:</strong> Edit and refine the AI suggestions to match your vision</li>
            <li><strong>Auto-populate Pages:</strong> Automatically add plot text to your pages with one click</li>
          </ul>
          <p>
            The plot planner uses advanced AI (powered by Google Gemini) to understand your story concept 
            and generate coherent, age-appropriate narratives that flow naturally from page to page.
          </p>
        </section>

        <section className="legal-section">
          <h2>Step 2: Generate Characters</h2>
          <p>
            Create unique, AI-generated characters that maintain consistent appearance throughout your storybook. 
            Our character generation system ensures your characters look the same on every page.
          </p>
          <ul>
            <li><strong>Character Description:</strong> Describe your character in natural language (e.g., "a tiny red dragon with big eyes")</li>
            <li><strong>Multiple Angles:</strong> Generate characters in 4 or 8 directional views for dynamic storytelling</li>
            <li><strong>Style Consistency:</strong> Characters are generated to match your chosen art style (children's book illustration, watercolor, digital art, etc.)</li>
            <li><strong>Instant Generation:</strong> Characters are created in seconds using GPT-Image-1-Mini</li>
            <li><strong>Reusable Library:</strong> All generated characters are saved to your library for easy reuse</li>
          </ul>
          <p>
            You can generate as many characters as needed for your story. Each character is stored in your 
            personal library and can be used across multiple pages and projects.
          </p>
        </section>

        <section className="legal-section">
          <h2>Step 3: Design Your Pages</h2>
          <p>
            Use our intuitive visual editor to bring your storybook to life. The drag-and-drop interface 
            makes it easy to arrange text and images exactly how you want them.
          </p>
          <ul>
            <li><strong>Drag-and-Drop:</strong> Click and drag characters, backgrounds, and text boxes to position them</li>
            <li><strong>Text Formatting:</strong> Customize font family, size, weight, color, and alignment</li>
            <li><strong>Image Controls:</strong> Resize, rotate, and layer images to create dynamic compositions</li>
            <li><strong>Background Options:</strong> Add solid colors, gradients, or AI-generated scene backgrounds</li>
            <li><strong>Multi-layer Support:</strong> Manage multiple elements with layering controls (bring forward, send backward)</li>
            <li><strong>Live Preview:</strong> See exactly how your page will look as you design</li>
            <li><strong>Page Navigation:</strong> Easily move between pages to maintain story flow and consistency</li>
          </ul>
          <p>
            The editor provides pixel-perfect control while remaining intuitive and user-friendly. 
            Your work is automatically saved as you create, so you never lose progress.
          </p>
        </section>

        <section className="legal-section">
          <h2>Step 4: Export and Publish</h2>
          <p>
            When your storybook is complete, export it in high-quality formats ready for publishing 
            on platforms like Amazon Kindle Direct Publishing (KDP).
          </p>
          <ul>
            <li><strong>High-Resolution Export:</strong> Generate print-quality PDFs and images</li>
            <li><strong>Format Options:</strong> Export individual pages or complete book files</li>
            <li><strong>KDP-Ready:</strong> Files are formatted to meet Amazon KDP specifications</li>
            <li><strong>Multiple Formats:</strong> Export as PDF, PNG, or JPEG based on your needs</li>
            <li><strong>Cover Page Support:</strong> Create and export front and back covers</li>
          </ul>
          <p>
            After exporting, you can upload directly to Amazon KDP or other self-publishing platforms. 
            Many of our users have successfully published their storybooks and are earning royalties.
          </p>
        </section>

        <section className="legal-section">
          <h2>Additional Features</h2>
          <h3>Asset Management</h3>
          <p>
            All your generated characters, backgrounds, and uploaded images are organized in easy-to-browse 
            galleries. Assets can be reused across different projects and pages.
          </p>

          <h3>AI Assistance</h3>
          <p>
            Throughout the creation process, AI tools are available to help you:
          </p>
          <ul>
            <li>Generate story plots and narrative arcs</li>
            <li>Create consistent character artwork</li>
            <li>Design atmospheric background scenes</li>
            <li>Suggest text improvements and edits</li>
          </ul>

          <h3>Collaboration & Storage</h3>
          <p>
            Your projects are securely stored in the cloud, accessible from any device. You can work 
            on your storybook anytime, anywhere, and your progress is always saved.
          </p>
        </section>

        <section className="legal-section">
          <h2>Getting Started</h2>
          <p>
            Ready to create your first storybook? Here's what you need to do:
          </p>
          <ol>
            <li><strong>Create an Account:</strong> Sign up with your email address (free to start)</li>
            <li><strong>Purchase Tokens:</strong> Buy AI generation tokens to unlock character and background generation</li>
            <li><strong>Start Creating:</strong> Use the wizard to set up your first storybook project</li>
            <li><strong>Follow the Steps:</strong> Plan, generate, design, and export your storybook</li>
            <li><strong>Publish:</strong> Upload to Amazon KDP or your preferred platform</li>
          </ol>
          <p>
            Most users complete their first storybook within a few hours, though you can take as much time 
            as you need. The platform is designed to be intuitive, even for those with no design experience.
          </p>
        </section>

        <section className="legal-section">
          <h2>Tips for Success</h2>
          <ul>
            <li><strong>Start Simple:</strong> Begin with a shorter book (8-12 pages) to learn the tools</li>
            <li><strong>Plan First:</strong> Use the plot planner to outline your story before generating characters</li>
            <li><strong>Be Specific:</strong> Detailed character descriptions lead to better AI-generated results</li>
            <li><strong>Iterate:</strong> Don't be afraid to regenerate characters or scenes until you're satisfied</li>
            <li><strong>Review AI Content:</strong> Always review and edit AI-generated text and images</li>
            <li><strong>Save Often:</strong> Although auto-save is enabled, manually save important milestones</li>
            <li><strong>Test Export Early:</strong> Export a test page to ensure quality meets your expectations</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>Need Help?</h2>
          <p>
            If you have questions or need assistance:
          </p>
          <p className="contact-info">
            <strong>Email:</strong> support@tinylittlefox.com<br />
            <strong>Website:</strong> <a href="https://tinylittlefox.com">https://tinylittlefox.com</a>
          </p>
          <p>
            We're here to help you bring your storybook ideas to life!
          </p>
        </section>

        <footer className="legal-footer">
          <p>Ready to start creating? <a href="/register">Get started now →</a></p>
        </footer>
      </div>
    </div>
  );
};

export default HowItWorks;
